import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useImmerReducer } from "use-immer";
import { Configuration } from "../../API/Configuration";
import { Users } from "../../API/Users";
import { deepClone } from "../../API/utils/deepClone";
import CustomDatePicker from "../../components/CustomDatePicker";
import GroupInput from "../../components/GroupInput";
import ReusableModal from "../../components/ReusableModal";
import { useManageAccount } from "../../customHooks/useManageAccount";
import { TDate } from "../../TDate";
import {
  initManageAccountState,
  manageAccountReducer,
  ManageAccountStateType,
} from "./manageAccount.reducer";
import Subscriptions from "./Subscriptions";
import { checkIssues, getProductCode, userTypes } from "./utils";
import { Products } from "../../API/Product";

type ManageAccountModalContentProps = {
  sellers: any[];
  initState: any;
  //#region - BACKUP
  // we use this "backup" to keep track of the preview product
  // in order to display warnings about the change.
  backup: any;
  //#endregion
};

const ManageAccountModalContent = forwardRef(
  (
    { initState, sellers, backup }: ManageAccountModalContentProps,
    manageAccountModalContentRef
  ) => {
    const { productOptions, setProductCode, getProductOptions } =
      useManageAccount();

    const [manageAccountState, dispatch] = useImmerReducer<
      ManageAccountStateType,
      any
    >(manageAccountReducer, initManageAccountState as any);

    useEffect(() => {
      if (initState == null) {
        dispatch({ type: "RESET_STATE", value: null });
      } else {
        dispatch({ type: "LOAD_STATE", value: initState });
      }
    }, [dispatch, initState]);

    const dailyReportOptions = useMemo(() => {
      if (manageAccountState?.level) {
        return getDailyReportAvailableOptions(
          manageAccountState?.level,
          manageAccountState?.productCode
        );
      } else {
        return [];
      }
    }, [manageAccountState?.level, manageAccountState?.productCode]);

    const [issuesPanel, setIssuesPanel] = useState<any>(null);
    const [permissions, setPermissions] = useState<
      {
        show: boolean;
        label: string;
        title: string;
        value: string;
        disabled: boolean;
        checked: boolean;
        message?: string;
        permId?: number;
      }[]
    >([]);

    const formatProductAvailabilityMSG = useCallback(
      async (availableList: string[]) => {
        if (availableList.length) {
          let str = [];
          let options = productOptions;
          let productDictionaries = null;

          if (!options || !options.length) {
            const productsAPI = new Products();

            const response = await productsAPI.getAllProducts();
            const products = response?.data.products;

            options = products.reduce((prev: any, current: any) => {
              prev[current.productCode] = current.codeName;

              return prev;
            }, {});

            productDictionaries = options;
          } else {
            productDictionaries = options.reduce((prev, current) => {
              prev[current.productCode] = current.label;

              return prev;
            }, {});
          }

          for (const productCode of availableList) {
            if (productDictionaries?.[productCode]) {
              str.push(productDictionaries?.[productCode]);
            }
          }

          return "* Only available in " + str.join(", ");
        }

        return "";
      },
      [productOptions]
    );

    const onLanfingGetPermissions = useCallback(async () => {
      const userAPI = new Users();
      const configAPI = new Configuration();
      const userId = initState?.id;

      try {
        const structures = await configAPI.getStructures();
        let userPermissions = [];

        if (userId) {
          userPermissions = await userAPI.fetchUserProperties(
            ["permissions"],
            [userId]
          );

          userPermissions = userPermissions?.data?.rows?.[0]?.permissions;
        }
        let permissionsVoices: {
          show: boolean;
          label: string;
          title: string;
          value: string;
          disabled: boolean;
          checked: boolean;
          message?: string;
          permId?: any;
        }[] = [];

        const userPermissionsMap: any = {};

        for (const obj of userPermissions) {
          if (obj?.permission) {
            userPermissionsMap[obj?.permission] = obj.id;
          }
        }

        for (const permission of structures.permissions) {
          const prototype: any = {
            value: permission.id,
            label: permission.name,
            title: permission.title,
            disabled: !permission.editable,
            show: permission.display,
            checked: permission.id in userPermissionsMap,
            message: undefined,
            permId: userPermissionsMap?.[permission.id] ?? null,
          };

          // TODO into the dispatch change the arr of strings into an arr of {id: permissionId, permission: THE PERMSISSION STRING}

          if (permission?.product != null) {
            prototype["message"] = await formatProductAvailabilityMSG(
              permission.product
            );
          }

          permissionsVoices.push(prototype);
        }

        const currentUserPermissions: { id?: number; permission: string }[] =
          [];

        for (const perm of permissionsVoices) {
          if (perm.checked) {
            currentUserPermissions.push({
              id: perm.permId,
              permission: perm.value,
            });
          }
        }

        // Get current user permissions in the state to avoid loosing it on saving
        dispatch({
          type: "SET_PERMISSIONS",
          value: currentUserPermissions,
        });
        setPermissions(permissionsVoices);
      } catch (error) {
        console.log(error);
      }
    }, [dispatch, formatProductAvailabilityMSG, initState?.id]);

    const onChangeWebApplication = useCallback(
      (e: any) => {
        if (manageAccountState.id) {
          // edit
          if (!backup?.product?.productCode) {
            throw new Error("something went wrong...");
          }
          const hasIssues = checkIssues(
            backup?.product?.productCode,
            getProductCode(productOptions, e.target?.value)
          );
          if (hasIssues != null) {
            setIssuesPanel(
              <ReusableModal
                modalTitle={
                  <Typography>
                    Issues on changing web applications from{" "}
                    {manageAccountState.productCode} to{" "}
                    {getProductCode(productOptions, e.target?.value)}
                  </Typography>
                }
                children={<Box>{getIssuesContent(hasIssues)}</Box>}
                isOpen={true}
                setIsOpen={() => setIssuesPanel(null)}
                buttons={[
                  <Button
                    onClick={() => {
                      setIssuesPanel(null);
                    }}
                  >
                    Cancel
                  </Button>,
                  <Button
                    onClick={() => {
                      dispatch({
                        type: "SET_PRODUCT_ID",
                        value: e.target.value,
                      });
                      const pCode = getProductCode(
                        productOptions,
                        e.target.value
                      );

                      setProductCode(pCode);
                      dispatch({
                        type: "SET_PRODUCT_CODE",
                        value: pCode,
                      });
                      setIssuesPanel(null);
                    }}
                    color="secondary"
                  >
                    Confirm
                  </Button>,
                ]}
              />
            );
          } else {
            dispatch({ type: "SET_PRODUCT_ID", value: e.target.value });
            const pCode = getProductCode(productOptions, e.target.value);

            setProductCode(pCode);
            dispatch({
              type: "SET_PRODUCT_CODE",
              value: pCode,
            });
          }
        } else {
          // create
          dispatch({ type: "SET_PRODUCT_ID", value: e.target.value });
          const pCode = getProductCode(productOptions, e.target.value);
          setProductCode(pCode);
          dispatch({
            type: "SET_PRODUCT_CODE",
            value: pCode,
          });
        }
      },
      [
        manageAccountState.id,
        manageAccountState.productCode,
        backup?.product?.productCode,
        dispatch,
        productOptions,
        setProductCode,
      ]
    );

    const onChangeAccountType = useCallback(
      async (e: any) => {
        dispatch({
          type: "SET_ACCOUNT_TYPE",
          value: e.target.value,
        });
        dispatch({
          type: "SET_CONVERSION_DATE",
          value: new Date().getTime(),
        });

        //#region
        // check if there are available products for the selected account type
        // if the product in the state is not in the available array then we set it to null.
        // for example if an account was subscriber but you set it to LEAD, if a product was selected
        // it'll have one of the product available for subscriber. no product is available for LEAD then we have to
        // set the product to null.

        // check product
        let _options = await getProductOptions(e.target.value);
        _options = _options.map((item: any) => item.value);
        if (
          manageAccountState.productId &&
          !_options.includes(manageAccountState.productId)
        ) {
          dispatch({
            type: "SET_PRODUCT_ID",
            value: null,
          });
          dispatch({
            type: "SET_PRODUCT_CODE",
            value: null,
          });
          setProductCode(null);
        }
        // check dailyReport
        _options = getDailyReportAvailableOptions(e.target.value, null).map(
          (item) => item.value
        );
        if (
          manageAccountState.dailyReport &&
          !_options.includes(manageAccountState.dailyReport)
        ) {
          dispatch({
            type: "SET_DAILY_REPORT",
            value: "DISABLED",
          });
        }
        //#endregion
      },
      [
        dispatch,
        getProductOptions,
        manageAccountState.dailyReport,
        manageAccountState.productId,
        setProductCode,
      ]
    );

    const _productOptions = useMemo(() => {
      const arr: any[] = productOptions
        .sort((a, b) =>
          a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
        )
        .map((item, index) => (
          <MenuItem key={index} value={item?.value}>
            <Typography
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.label}
            </Typography>
          </MenuItem>
        ));
      return [
        <MenuItem key={"-"} disabled value={"-"}>
          <Typography>Select an application</Typography>
        </MenuItem>,
        ...arr,
      ];
    }, [productOptions]);

    const handlePermissionChange = useCallback(
      (event: any, permissionValue: string) => {
        const checked = event.target.checked ?? false;

        setPermissions((current) => {
          let newState = [];
          let permissions = [];

          for (const permission of current) {
            if (permission.value === permissionValue) {
              permission.checked = checked;
            }

            if (permission.checked) {
              const permissionObj = {
                id: permission.permId,
                permission: permission.value,
              };

              if (permissionObj.id == null) {
                delete permissionObj.id;
              }

              permissions.push(permissionObj);
            }

            newState.push(permission);
          }

          dispatch({ type: "SET_PERMISSIONS", value: permissions });

          return newState;
        });
      },
      [dispatch]
    );

    const [fieldValidation, setFieldValidation] = useState({
      firstName: true,
      lastName: true,
      group: true,
      email: true,
      level: true,
      creationTime: true,
      updateDate: true,
      owner: true,
      productId: true,
      dailyReport: true,
    });
    const resetValidation = useCallback(() => {
      setFieldValidation({
        firstName: true,
        lastName: true,
        group: true,
        email: true,
        level: true,
        creationTime: true,
        updateDate: true,
        owner: true,
        productId: true,
        dailyReport: true,
      });
    }, []);

    const fieldValidator = useCallback((data: any) => {
      const validatation = {
        firstName: true,
        lastName: true,
        group: true,
        email: true,
        level: true,
        creationTime: true,
        updateDate: true,
        owner: true,
        productId: true,
        dailyReport: true,
      };

      if (!data.firstName || data.firstName?.length < 1) {
        validatation.firstName = false;
      } else {
        validatation.firstName = true;
      }

      if (!data.lastName || data.lastName?.length < 1) {
        validatation.lastName = false;
      } else {
        validatation.lastName = true;
      }

      var validEmailRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      if (
        data.email &&
        data.email.length > 0 &&
        data.email.match(validEmailRegex)
      ) {
        validatation.email = true;
      } else {
        validatation.email = false;
      }

      if (!data.group || data.group.length < 1) {
        validatation.group = false;
      } else {
        validatation.group = true;
      }

      // if (!data.dailyReport || data.dailyReport.length < 1) {
      //   validatation.dailyReport = false;
      // } else {
      //   validatation.dailyReport = true;
      // }

      if (!data.level || data.level.length < 1) {
        validatation.level = false;
      } else {
        validatation.level = true;
      }
      if (data.creationTime) {
        const _date: any = new Date(data.creationTime);
        if (!isNaN(_date)) {
          validatation.creationTime = true;
        } else {
          validatation.creationTime = false;
        }
      } else {
        validatation.creationTime = false;
      }

      if (data.owner != null) {
        validatation.owner = true;
      } else {
        validatation.owner = false;
      }

      if (data.productId != null) {
        validatation.productId = true;
      } else {
        validatation.productId = false;
      }
      setFieldValidation(validatation);
      if (Object.values(validatation).every((item) => item === true)) {
        return true;
      } else {
        return false;
      }
    }, []);

    const [allowCorrection, setAllowCorrection] = useState(false);
    useEffect(() => {
      if (allowCorrection) {
        fieldValidator(manageAccountState);
      }
    }, [allowCorrection, fieldValidator, manageAccountState]);

    useImperativeHandle(
      manageAccountModalContentRef,
      () => ({
        loadState: (state: any) => {
          dispatch({ type: "LOAD_STATE", value: state });
        },
        getDispatcher: () => dispatch,
        getState: () => deepClone(manageAccountState),
        resetValidation: () => resetValidation(),
        validate: () => fieldValidator(manageAccountState),
        allowCorrection: (allow: boolean) => setAllowCorrection(allow),
      }),
      [dispatch, fieldValidator, manageAccountState, resetValidation]
    );

    useEffect(() => {
      onLanfingGetPermissions();
    }, [onLanfingGetPermissions]);

    return (
      <Box display={"flex"} flexDirection={"column"} gap={1} pb={2}>
        {issuesPanel}
        <Box gap={1} display={"flex"} flexDirection={"column"}>
          <Box display={"flex"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={1}
              borderRadius={"6px"}
              border={"1px solid #B0B0B0"}
              boxShadow={3}
              flex={4}
            >
              <Box borderBottom={"1px solid #B0B0B0"} px={2} py={1}>
                <Typography>
                  <strong>Account</strong>
                </Typography>
              </Box>
              <Box p={1} gap={2} display={"flex"} flexDirection={"column"}>
                <Box display={"flex"} gap={1} width={"100%"}>
                  <Box width={"50%"}>
                    <TextField
                      color="secondary"
                      fullWidth
                      error={!fieldValidation.firstName}
                      helperText={
                        !fieldValidation.firstName &&
                        "First name can't be an empty string"
                      }
                      InputProps={{
                        autoComplete: "off",
                      }}
                      sx={{ flex: 1 }}
                      onChange={(e) => {
                        let _firstname: string = e.target.value;
                        _firstname = _firstname.trim();
                        dispatch({
                          type: "SET_FIRST_NAME",
                          value: _firstname,
                        });
                      }}
                      label={"First name"}
                      size="small"
                      defaultValue={backup?.firstName}
                    />
                  </Box>
                  <Box width={"50%"}>
                    <TextField
                      fullWidth
                      color="secondary"
                      error={!fieldValidation.lastName}
                      helperText={
                        !fieldValidation.lastName &&
                        "Last name can't be an empty string"
                      }
                      sx={{ flex: 1 }}
                      onChange={(e) => {
                        let _lastname = e.target.value;
                        _lastname = _lastname.trim();
                        dispatch({
                          type: "SET_LAST_NAME",
                          value: _lastname,
                        });
                      }}
                      label={"Last name"}
                      size="small"
                      defaultValue={backup?.lastName}
                      InputProps={{
                        autoComplete: "off",
                      }}
                    />
                  </Box>
                </Box>
                <Box display={"flex"} width={"100%"}>
                  <Box width={"100%"}>
                    <TextField
                      fullWidth
                      color="secondary"
                      error={!fieldValidation.email}
                      helperText={
                        !fieldValidation.email && "Email is not valid"
                      }
                      onChange={(e) => {
                        let _email = e.target.value;
                        _email = _email.trim();
                        dispatch({ type: "SET_EMAIL", value: _email });
                      }}
                      label={"Email"}
                      size="small"
                      defaultValue={backup?.email}
                      InputProps={{
                        autoComplete: "off",
                      }}
                    />
                  </Box>
                </Box>
                <Box display={"flex"} width={"50%"}>
                  <GroupInput
                    value={manageAccountState.group}
                    setGroup={(value: string) =>
                      dispatch({ type: "SET_CLIENT", value: value })
                    }
                    validationGroup={!fieldValidation.group}
                  />
                </Box>
                <Box display={"flex"} width={"100%"} gap={1}>
                  <Box display={"flex"} width={"50%"}>
                    <CustomDatePicker
                      label="Acquisition Date"
                      value={manageAccountState.creationTime}
                      onChange={(dateInMiliseconds: any) => {
                        let _date = TDate.dateToDays(dateInMiliseconds);
                        _date = TDate.daysToMilliseconds(_date);
                        dispatch({
                          type: "SET_ACQUISITION_DATE",
                          value: _date,
                        });
                      }}
                    />
                  </Box>
                  {manageAccountState.id && (
                    <Box display={"flex"} width={"50%"}>
                      <CustomDatePicker
                        label="Conversion Date"
                        value={manageAccountState.levelUpdate}
                        onChange={(dateInMiliseconds: any) => {
                          let _date = TDate.dateToDays(dateInMiliseconds);
                          _date = TDate.daysToMilliseconds(_date);
                          dispatch({
                            type: "SET_CONVERSION_DATE",
                            value: _date,
                          });
                        }}
                      />
                    </Box>
                  )}
                </Box>
                <FormControl
                  sx={{ width: "50%" }}
                  error={!fieldValidation.owner}
                >
                  <InputLabel shrink={true} color="secondary" size={"small"}>
                    Owner
                  </InputLabel>
                  <Select
                    label={"Owner"}
                    color="secondary"
                    slotProps={{ input: { color: "secondary" } }}
                    size="small"
                    value={manageAccountState.owner ?? "-"}
                    onChange={(e) =>
                      dispatch({ type: "SET_OWNER", value: e.target.value })
                    }
                  >
                    {!manageAccountState.owner && (
                      <MenuItem key={"-"} value={"-"} disabled>
                        <Typography>Select owner</Typography>
                      </MenuItem>
                    )}
                    {sellers.map((item, index) => (
                      <MenuItem key={index} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box
              flex={2}
              display={"flex"}
              flexDirection={"column"}
              gap={1}
              border={"1px solid #B0B0B0"}
              borderRadius={"6px"}
              boxShadow={3}
            >
              <Box borderBottom={"1px solid #B0B0B0"} px={2} py={1}>
                <Typography>
                  <strong>Application</strong>
                </Typography>
              </Box>
              <Box
                flex={1}
                display={"flex"}
                p={1}
                flexDirection={"column"}
                gap={2}
              >
                <Box display={"flex"} gap={1} alignItems={"center"}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          color="secondary"
                          checked={manageAccountState.active ?? false}
                          onChange={(e) =>
                            dispatch({
                              type: "SET_ACTIVE",
                              value: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Active"
                    />
                  </Box>
                </Box>
                <Box display={"flex"} gap={1}>
                  <FormControl sx={{ flex: 1 }} error={!fieldValidation.level}>
                    <InputLabel color="secondary" size={"small"}>
                      <Typography>Account type</Typography>
                    </InputLabel>
                    <Select
                      color="secondary"
                      slotProps={{ input: { color: "secondary" } }}
                      size="small"
                      sx={{ maxWidth: "200px" }}
                      onChange={onChangeAccountType}
                      label={"Account type"}
                      value={manageAccountState.level ?? "-"}
                    >
                      {!manageAccountState.level && (
                        <MenuItem disabled key={"-"} value={"-"}>
                          Select account type
                        </MenuItem>
                      )}
                      {userTypes
                        .sort((a, b) =>
                          a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
                        )
                        .map((item, index) => (
                          <MenuItem key={index} value={item?.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
                {!(
                  productOptions.length === 0 &&
                  manageAccountState?.level != null
                ) && (
                  <FormControl error={!fieldValidation.productId}>
                    <InputLabel color="secondary" size={"small"}>
                      <Typography>Web application</Typography>
                    </InputLabel>
                    <Select
                      color="secondary"
                      sx={{ maxWidth: "200px" }}
                      slotProps={{
                        input: {
                          color: "secondary",
                        },
                      }}
                      size="small"
                      disabled={
                        manageAccountState?.level == null ||
                        productOptions.length === 0
                      }
                      placeholder="select and application"
                      label={"Web application"}
                      onChange={onChangeWebApplication}
                      value={manageAccountState.productId ?? "-"}
                    >
                      {manageAccountState?.level &&
                      productOptions.length > 0 ? (
                        _productOptions
                      ) : (
                        <MenuItem disabled value={"-"}>
                          <Typography
                            title={
                              "Select an account type to see available options"
                            }
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            Select an account type to see available options
                          </Typography>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
                <FormControl
                  sx={{ flex: 1 }}
                  error={!fieldValidation.dailyReport}
                >
                  <InputLabel shrink={true} color="secondary" size={"small"}>
                    <Typography>Daily report</Typography>
                  </InputLabel>
                  <Select
                    label={"Daily report"}
                    color="secondary"
                    sx={{ maxWidth: "200px" }}
                    slotProps={{ input: { color: "secondary" } }}
                    size="small"
                    disabled={manageAccountState?.level == null}
                    onChange={(e) => {
                      dispatch({
                        type: "SET_DAILY_REPORT",
                        value: e.target.value,
                      });
                    }}
                    value={manageAccountState.dailyReport}
                  >
                    {(manageAccountState?.level &&
                      manageAccountState?.productId &&
                      productOptions.length > 0) ||
                    (manageAccountState?.level &&
                      !manageAccountState?.productId &&
                      productOptions.length === 0) ? (
                      dailyReportOptions.map((item, index) => (
                        <MenuItem key={index} value={item?.value}>
                          <Typography>{item.label}</Typography>
                        </MenuItem>
                      ))
                    ) : manageAccountState?.level &&
                      productOptions.length > 0 &&
                      !manageAccountState?.productId ? (
                      <MenuItem disabled selected value={"DISABLED"}>
                        <Typography
                          title="Select a web application to see available options"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Select a web application to see available options
                        </Typography>
                      </MenuItem>
                    ) : (
                      <MenuItem disabled selected value={"DISABLED"}>
                        <Typography
                          title={
                            "Select an account type to see available options"
                          }
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Select an account type to see available options
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                {permissions.length && (
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    borderTop={"1px solid #B0B0B0"}
                    mt={1}
                  >
                    <Typography sx={{ marginTop: "8px" }}>
                      Permissions
                    </Typography>
                    <Box
                      component={"ul"}
                      display={"flex"}
                      flexDirection={"column"}
                      sx={{ listStyle: "none", paddingLeft: 0 }}
                    >
                      {permissions
                        .sort((a, b) =>
                          a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
                        )
                        .map((permission) => {
                          return permission.show ? (
                            <li key={permission.value}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    disabled={permission.disabled}
                                    size="small"
                                    color="secondary"
                                    checked={permission.checked}
                                    onChange={(e) =>
                                      handlePermissionChange(
                                        e,
                                        permission.value
                                      )
                                    }
                                  />
                                }
                                title={permission.title}
                                label={
                                  <Box>
                                    <Typography>{permission.label}</Typography>
                                    {permission.message && (
                                      <Typography sx={{ fontSize: "0.5vw" }}>
                                        {permission.message}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </li>
                          ) : (
                            <Fragment key={permission.value}></Fragment>
                          );
                        })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {!manageAccountState?.productId ? null : (
              <Box
                flex={1}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
                border={"1px solid #B0B0B0"}
                borderRadius={"6px"}
                boxShadow={3}
              >
                <Box borderBottom={"1px solid #B0B0B0"} px={2} py={1}>
                  <Typography>
                    <strong>Subscriptions</strong>
                  </Typography>
                </Box>
                <Box p={1}>
                  <Subscriptions />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
);
const getIssuesContent = (obj: any) => {
  const arr: any[] = [];
  const keys = Object.keys(obj);
  keys.forEach((key, index) => {
    arr.push(
      <ul key={index}>
        {obj[key].map((phrase: string) => (
          <li>
            <Typography> {phrase}</Typography>
          </li>
        ))}
      </ul>
    );
  });
  return arr;
};

export default ManageAccountModalContent;

const getDailyReportAvailableOptions = (
  level: string,
  productCode: string | null
) => {
  let arrOptions: any[] = [];
  if (level) {
    switch (level) {
      case "LEAD":
        arrOptions = [
          { label: "Lead", value: "LEAD" },
          { label: "Disabled", value: "DISABLED" },
        ];
        break;
      case "INTERNAL":
        arrOptions = [
          { label: "Daily Report V1.0", value: "V1" },
          { label: "Daily Report V2.0", value: "V2" },
          { label: "Daily Report V1.0 + V2.0", value: "V1V2" },
          { label: "Lead", value: "LEAD" },
          { label: "Disabled", value: "DISABLED" },
        ];
        break;
      default:
        arrOptions = [
          { label: "Daily Report V1.0", value: "V1" },
          { label: "Daily Report V2.0", value: "V2" },
          { label: "Disabled", value: "DISABLED" },
        ];
        break;
    }
    if (productCode) {
      switch (productCode) {
        case "DISPERSION":
          arrOptions = [];
          break;
        case "RISE":
          arrOptions = arrOptions.filter((item) => item.value !== "V2");
          break;
      }
    }
    return arrOptions;
  }
  return [];
};
